/**
 * Row + header action config for ResizableTable in related-list mode (RelatedListView, search widget tables).
 */

export const DEFAULT_RELATED_LIST_ACTIONS = [
  'new',
  'edit',
  'clone',
  'delete',
  'bulk_delete',
  'import',
  'export'
];

/**
 * @param {string[]|undefined} raw
 * @param {string} [targetObjectName]
 * @returns {string[]}
 */
export function normalizeAllowedRelatedListActions(raw, targetObjectName) {
  let base =
    Array.isArray(raw) && raw.length > 0 ? raw : [...DEFAULT_RELATED_LIST_ACTIONS];
  let normalized = base
    .map((a) => String(a).toLowerCase())
    .filter((a) => DEFAULT_RELATED_LIST_ACTIONS.includes(a));
  if (normalized.length === 0) normalized = [...DEFAULT_RELATED_LIST_ACTIONS];
  normalized = Array.from(new Set(normalized));

  if (targetObjectName && targetObjectName.toLowerCase() === 'email_log') {
    normalized = normalized.filter((a) => a !== 'new' && a !== 'edit');
  }

  if (targetObjectName && targetObjectName.toLowerCase() === 'user') {
    normalized = normalized.filter((a) => a !== 'delete');
  }

  return normalized;
}

/**
 * Same row action set as RelatedListView.getDynamicActions (non-attachment, non-history).
 * @param {string[]} allowedActions — normalized
 * @param {object} [opts]
 * @param {string} [opts.objectName]
 */
export function buildRelatedListRowActions(allowedActions, opts = {}) {
  const { objectName } = opts;
  const isAttachmentTab = false;
  const isHistoryObject = false;

  if (isAttachmentTab) {
    return [];
  }
  if (isHistoryObject) {
    return [];
  }

  const actions = [
    { label: 'View', icon: 'fas fa-eye', action: 'view' }
  ];

  if (allowedActions.includes('edit')) {
    actions.push({ label: 'Edit', icon: 'fas fa-edit', action: 'edit' });
  }
  if (allowedActions.includes('clone')) {
    actions.push({ label: 'Clone', icon: 'fas fa-clone', action: 'clone' });
  }
  if (allowedActions.includes('delete')) {
    actions.push({ label: 'Delete', icon: 'fas fa-trash', action: 'delete' });
  }

  if (objectName?.toLowerCase() === 'user') {
    return actions.filter((a) => a.action !== 'delete');
  }

  return actions;
}

/**
 * Same header action metadata as RelatedListView.getHeaderActions (ResizableTable wires onClick by label).
 * @param {string[]} allowedActions — normalized
 * @param {{ isAttachmentTab?: boolean, isHistoryObject?: boolean, selectedCount?: number }} opts
 */
export function buildRelatedListHeaderActions(allowedActions, opts = {}) {
  const {
    isAttachmentTab = false,
    isHistoryObject = false,
    selectedCount = 0
  } = opts;
  const actions = [];

  if (!isHistoryObject && allowedActions.includes('new')) {
    actions.push({
      label: isAttachmentTab ? 'Upload' : 'New',
      icon: isAttachmentTab ? 'fas fa-upload' : 'fas fa-plus',
      variant: 'primary'
    });
  }

  if (!isHistoryObject && allowedActions.includes('bulk_delete')) {
    actions.push({
      label: 'Bulk Delete',
      icon: 'fas fa-trash',
      variant: 'danger',
      disabled: selectedCount === 0
    });
  }

  if (!isHistoryObject && allowedActions.includes('import')) {
    actions.push({
      label: 'Import',
      icon: 'fas fa-file-import',
      variant: 'outline-primary'
    });
  }

  if (allowedActions.includes('export')) {
    actions.push({
      label: 'Export',
      icon: 'fas fa-download',
      variant: 'primary',
      disabled: selectedCount === 0
    });
  }

  return actions;
}
